import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FormList from '../components/FormList';
import HiredEquipmentDrafts from '../components/HiredEquipmentDrafts';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { PageHeader, Tab, Tabs } from '@/components/ui';
import { type ApprovalFormType } from '../config/approvalFormTypes';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { usePersistedListState } from '../../../hooks/usePersistedListState';
import NewHiredEquipmentModal from '../../hired-equipment/components/NewHiredEquipmentModal';
import { persistHiredEquipmentAnchor } from '../../hired-equipment/utils/anchorStorage';
import type { HiredEquipmentAnchor } from '../../hired-equipment/types';

/** sessionStorage page key for the remembered list state, shared with FormList (V2-024). */
const SUBMISSIONS_PAGE_KEY = 'submissions';

const FormsView: React.FC = () => {
  const { loading } = useAuth();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // The type tab is kept for the sign-in with the rest of the list state (V2-024, FLT-09).
  const [selectedFormType, setSelectedFormType] = usePersistedListState<ApprovalFormType>(
    SUBMISSIONS_PAGE_KEY,
    'type',
    'all'
  );
  const [refreshKey, setRefreshKey] = useState(0);
  const [showHeModal, setShowHeModal] = useState(false);
  useDocumentTitle('Submissions');

  // V2-038: hired equipment is entered from here – a header button and a Drafts tab.
  const canCreateHiredEquipment = hasPermission('submission:he_manage');
  const tab = canCreateHiredEquipment && searchParams.get('tab') === 'drafts' ? 'drafts' : 'submissions';

  const selectTab = (key: string | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (key === 'drafts') next.set('tab', 'drafts');
        else next.delete('tab');
        return next;
      },
      { replace: true }
    );
  };

  const handleContinue = (anchor: HiredEquipmentAnchor) => {
    persistHiredEquipmentAnchor(anchor);
    setShowHeModal(false);
    navigate('/hired-equipment/grid', { state: { anchor } });
  };

  if (loading) {
    return <div className="tw-w-full tw-p-4">Loading...</div>;
  }

  const list = (
    <FormList key={refreshKey} formType={selectedFormType} onFormTypeChange={setSelectedFormType} />
  );

  return (
    // Fill the app shell and scroll the list region internally.
    <div className="tw-w-full tw-flex tw-flex-col tw-flex-1 tw-min-h-0 tw-p-4">
      {/* No Search button (V2-077): search is always visible in the filter panel header. */}
      <PageHeader
        title="Submissions"
        actions={[
          {
            key: 'refresh',
            label: 'Refresh',
            icon: 'bi-arrow-clockwise',
            intent: 'secondary',
            onClick: () => setRefreshKey((k) => k + 1),
          },
          {
            key: 'new-he',
            label: 'New Hired Equipment',
            icon: 'bi-plus-lg',
            intent: 'primary',
            alwaysShowLabel: true,
            hidden: !canCreateHiredEquipment,
            // M25: disabled while the modal is open, so a second click cannot close it.
            disabled: showHeModal,
            onClick: () => {
              if (!showHeModal) setShowHeModal(true);
            },
          },
        ]}
      />

      <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw--mx-4 tw-px-4">
        {canCreateHiredEquipment ? (
          <Tabs activeKey={tab} onSelect={selectTab} className="tw-mb-3">
            <Tab eventKey="submissions" title="Submissions">
              {list}
            </Tab>
            <Tab eventKey="drafts" title="Drafts">
              <HiredEquipmentDrafts key={refreshKey} />
            </Tab>
          </Tabs>
        ) : (
          list
        )}
      </div>

      {canCreateHiredEquipment && (
        <NewHiredEquipmentModal
          show={showHeModal}
          onHide={() => setShowHeModal(false)}
          onContinue={handleContinue}
        />
      )}
    </div>
  );
};

export default FormsView;
